/**
 * ABET brand loading state — an alpha casino chip flipping on its edge.
 *
 * The mark is the app's own wordmark glyph, taken from the shell:
 *   src/App.jsx:399      <span className="app-logo-alpha">&alpha;</span>
 *   src/App.css:277-285  font-family:'Times New Roman',serif; font-weight:700;
 *                        color:var(--accent-secondary); text-shadow:0 0 18px rgba(253,181,21,.28)
 *
 * Both faces carry the alpha so it stays legible mid-flip, and the rim notches make it
 * read as a chip rather than a coin. Colour comes from --accent-secondary with a literal
 * fallback so it renders correctly even if the variable is not in scope.
 */
export default function AbetChipLoader({ message = 'Loading…', size = 76 }) {
  const gold = 'var(--accent-secondary, #FDB515)'
  const face = {
    position: 'absolute',
    inset: 0,
    borderRadius: '50%',
    display: 'grid',
    placeItems: 'center',
    backfaceVisibility: 'hidden',
    border: `3px solid ${gold}`,
    background: 'radial-gradient(circle at 34% 30%, #1b2431 0%, #0d131c 62%, #070B0F 100%)',
    boxShadow: '0 0 24px rgba(253,181,21,0.28), inset 0 0 16px rgba(253,181,21,0.10)',
  }
  const alpha = {
    fontFamily: "'Times New Roman', serif",
    fontSize: Math.round(size * 0.52),
    fontWeight: 700,
    color: gold,
    textShadow: '0 0 18px rgba(253,181,21,0.5)',
    lineHeight: 0.88,
    transform: 'translateY(-2px)',
  }
  const notches = Array.from({ length: 8 }, (_, i) => (
    <span
      key={i}
      style={{
        position: 'absolute',
        left: '50%',
        top: 2,
        width: Math.max(6, Math.round(size * 0.09)),
        height: Math.max(8, Math.round(size * 0.12)),
        marginLeft: -Math.max(3, Math.round(size * 0.045)),
        borderRadius: 2,
        background: 'rgba(253,181,21,0.85)',
        transformOrigin: `50% ${size / 2 - 2}px`,
        transform: `rotate(${i * 45}deg)`,
      }}
    />
  ))

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, padding: '38px 0' }}>
      <div style={{ perspective: 620, width: size, height: size }}>
        <div
          style={{
            position: 'relative',
            width: '100%',
            height: '100%',
            transformStyle: 'preserve-3d',
            animation: 'abet-chip-flip 2.4s cubic-bezier(.45,.05,.35,1) infinite',
          }}
        >
          <div style={face}>{notches}<span style={alpha}>&alpha;</span></div>
          <div style={{ ...face, transform: 'rotateY(180deg)' }}>{notches}<span style={alpha}>&alpha;</span></div>
        </div>
      </div>
      <span style={{ fontSize: 13, letterSpacing: '0.04em', opacity: 0.75 }}>{message}</span>
      <style>{`
        @keyframes abet-chip-flip {
          0%   { transform: rotateY(0deg)   translateY(0) }
          45%  { transform: rotateY(540deg) translateY(-10px) }
          70%  { transform: rotateY(720deg) translateY(0) }
          78%  { transform: rotateY(720deg) translateY(-3px) }
          86%  { transform: rotateY(720deg) translateY(0) }
          100% { transform: rotateY(720deg) translateY(0) }
        }
        @media (prefers-reduced-motion: reduce) {
          div[style*="preserve-3d"] { animation: none !important }
        }
      `}</style>
    </div>
  )
}
